import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DEFAULT_MEASUREMENT_SYSTEM,
  isMeasurementSystem,
  type MeasurementSystem,
} from '@/shared/domain'
import { useAuth } from '@/shared/ui/hooks'
import type { LoadUserProfile, UpdateMyProfile } from '@/modules/auth/use-cases'
import { UnitsContext } from './units-context'

export const UNITS_STORAGE_KEY = 'cc-units'

function readStoredSystem(): MeasurementSystem {
  if (typeof window === 'undefined') return DEFAULT_MEASUREMENT_SYSTEM
  try {
    const stored = window.localStorage.getItem(UNITS_STORAGE_KEY)
    return isMeasurementSystem(stored) ? stored : DEFAULT_MEASUREMENT_SYSTEM
  } catch {
    return DEFAULT_MEASUREMENT_SYSTEM
  }
}

function writeStoredSystem(system: MeasurementSystem): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(UNITS_STORAGE_KEY, system)
  } catch {
    return
  }
}

type UnitsProviderProps = {
  onLoadUserProfile: LoadUserProfile
  onUpdateMyProfile: UpdateMyProfile
  children: React.ReactNode
}

export function UnitsProvider({
  onLoadUserProfile,
  onUpdateMyProfile,
  children,
}: UnitsProviderProps) {
  const { user } = useAuth()
  const [system, setSystemState] = useState<MeasurementSystem>(readStoredSystem)
  const systemRef = useRef(system)

  const applySystem = useCallback((next: MeasurementSystem) => {
    systemRef.current = next
    setSystemState(next)
    writeStoredSystem(next)
  }, [])

  useEffect(() => {
    if (!user) return
    let cancelled = false
    onLoadUserProfile()
      .then((result) => {
        if (cancelled) return
        const next = result.user.measurementSystem
        if (!isMeasurementSystem(next)) return
        applySystem(next)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [user, onLoadUserProfile, applySystem])

  const setSystem = useCallback(
    async (next: MeasurementSystem) => {
      const previous = systemRef.current
      if (next === previous) return
      applySystem(next)
      try {
        await onUpdateMyProfile({ measurementSystem: next })
      } catch (error) {
        applySystem(previous)
        throw error
      }
    },
    [applySystem, onUpdateMyProfile],
  )

  const value = useMemo(
    () => ({ system, isMetric: system === 'metric', setSystem }),
    [system, setSystem],
  )

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>
}
